import { Component, computed, inject, input, signal, OnInit } from '@angular/core';
import { form, FormField, required, validate } from '@angular/forms/signals';
import { UiModule } from '../../../components/ui.module';
import { EdgeDeviceService } from '../../../service/edge-device.service';
import { NotifyService } from '../../../service/notify.service';
import { EdgeDevice, GenericNetworkConfig } from '../../../types/edge-device';

const IPV4_REGEX = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
const IPV4_CIDR_REGEX = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)(\/([0-9]|[12]\d|3[0-2]))$/;

export function isValidIpv4(value: string): boolean {
  return IPV4_REGEX.test(value);
}

export function isValidIpv4WithCidr(value: string): boolean {
  return IPV4_CIDR_REGEX.test(value);
}

const defaultForm: () => Required<GenericNetworkConfig> = () => ({
  mode: 'dhcp',
  address: '',
  gateway: '',
  dns: '',
});

const validators = () => (schemaPath) => {
  required(schemaPath.configurationMode);

  validate(schemaPath.address, (ctx) => {
    const value = ctx.value() as string;
    if (!value) return undefined;
    if (isValidIpv4WithCidr(value)) return undefined;
    return { kind: 'ipAddress', message: 'Must be a valid IPv4 address with CIDR (e.g. 192.168.1.0/24)' };
  });

  validate(schemaPath.gateway, (ctx) => {
    const value = ctx.value() as string;
    if (!value) return undefined;
    if (isValidIpv4(value)) return undefined;
    return { kind: 'ipAddress', message: 'Must be a valid IPv4 address (e.g. 192.168.1.1)' };
  });

  validate(schemaPath.dns, (ctx) => {
    const value = ctx.value() as string;
    if (!value) return undefined;
    if (isValidIpv4(value)) return undefined;
    return { kind: 'ipAddress', message: 'Must be a valid IPv4 address (e.g. 8.8.8.8)' };
  });
};

@Component({
  selector: 'app-edge-device-network-management',
  templateUrl: './edge-device-network-management.component.html',
  styleUrls: ['./edge-device-network-management.component.scss'],
  standalone: true,
  imports: [UiModule, FormField],
})
export class EdgeDeviceNetworkManagementComponent implements OnInit {
  private edgeDeviceService = inject(EdgeDeviceService);
  private notifyService = inject(NotifyService);

  public edgeDevice = input.required<EdgeDevice>();

  public view = signal<'primary' | 'secondary'>('primary');

  public primary = signal<Required<GenericNetworkConfig>>(defaultForm());
  public secondary = signal<Required<GenericNetworkConfig>>(defaultForm());

  public primaryForm = form(this.primary, validators());
  public secondaryForm = form(this.secondary, validators());

  public currentForm = computed(() => (this.view() === 'primary' ? this.primaryForm : this.secondaryForm));

  public canSave = computed(() => {
    if (this.primary().mode === 'static') {
      if (!this.primary().address) {
        return false;
      }
      if (!this.primary().gateway) {
        return false;
      }
      if (!this.primary().dns) {
        return false;
      }
    }

    if (this.secondary().mode === 'static' && !this.secondary().address) {
      return false;
    }

    if (this.primaryForm().invalid() || this.secondaryForm().invalid()) {
      return false;
    }

    return true;
  });

  ngOnInit() {
    const currentConfig = this.edgeDevice().config?.network;
    const networkConfig = { eth0: currentConfig?.eth0 ?? defaultForm(), usb_eth: currentConfig?.usb_eth ?? defaultForm() };

    this.primary.update(() => this.reformatNetworkConfigForApi(networkConfig?.eth0));
    this.secondary.update(() => this.reformatNetworkConfigForApi(networkConfig?.usb_eth));
  }

  private reformatNetworkConfigForApi(networkConfig: Partial<GenericNetworkConfig> = {}): Required<GenericNetworkConfig> {
    return {
      ...networkConfig,
      mode: networkConfig.mode ?? 'dhcp',
      address: networkConfig.address ?? '',
      gateway: networkConfig.gateway ?? '',
      dns: networkConfig.dns ?? '',
    };
  }

  resetSubFormData() {
    if (this.view() === 'primary') {
      this.primary.update((config) => ({ ...config, address: '', gateway: '', dns: '' }));
    } else {
      this.secondary.update((config) => ({ ...config, address: '', gateway: '', dns: '' }));
    }
  }

  save() {
    const finalNetworkConfig: { eth0: GenericNetworkConfig; usb_eth: GenericNetworkConfig } = {
      eth0: this.primary(),
      usb_eth: this.secondary(),
    };

    this.edgeDeviceService.updateNetworkConfiguration(this.edgeDevice().id, finalNetworkConfig).subscribe({
      next: () => {
        this.notifyService.notify('Network configuration updated successfully!');
      },
      error: () => {
        this.notifyService.notify('Network configuration failed to update!');
      },
    });
  }
}
